/**
 * `openSse()` — the one server-sent-events client (spec §14.7).
 *
 *   "openSse() utility handles GET streams (run, training) and the POST chat
 *    stream: Last-Event-ID resume, exponential backoff with jitter <= 30 s,
 *    45 s silence = dead, terminal 401/404, POST streams never reconnect;
 *    concurrent stream cap 3 consoles + 1 training + 1 chat."
 *
 * It is built on `fetch` + a `ReadableStream` reader rather than the platform
 * `EventSource` for three reasons: `EventSource` cannot set `Last-Event-ID`
 * explicitly (it only replays what it saw itself, which loses the cold-open
 * cursor from `GET /runs/:id/events/history`), cannot send a POST body, and
 * cannot distinguish a 404 from a network blip — and §13 makes 401/404
 * terminal.
 *
 * Resume is exact because the SSE `id:` field IS the Valkey stream id: the API
 * answers `Last-Event-ID: 1712-0` with `XRANGE (1712-0 +`, so a reconnect
 * yields no gap and no duplicate.
 */

/** The stream kinds the cap is counted over. */
export type SseKind = "run" | "training" | "chat";

/** §14.7: "concurrent stream cap 3 consoles + 1 training + 1 chat". */
export const SSE_STREAM_LIMITS: Readonly<Record<SseKind, number>> = {
  run: 3,
  training: 1,
  chat: 1,
};

/** §13: a stream that ends with this event is over; do not reconnect. */
export const DEFAULT_TERMINAL_EVENTS: readonly string[] = ["run_ended"];

/** §14.7: 45 s of complete silence — not even a keepalive comment — is dead. */
export const DEFAULT_SILENCE_MS = 45_000;
export const DEFAULT_MAX_BACKOFF_MS = 30_000;
export const DEFAULT_BASE_BACKOFF_MS = 500;

/** §13: the API answers an unauthenticated or foreign stream with these. */
const TERMINAL_STATUS = new Set([400, 401, 403, 404, 410, 422]);

export interface SseMessage {
  /** The `id:` field — the Valkey stream id. Absent on comment-only frames. */
  id?: string;
  /** The `event:` field, defaulting to "message" as the SSE spec requires. */
  event: string;
  /** The `data:` field, with multiple data lines joined by a newline. */
  data: string;
}

export interface SseFailure {
  kind: "http" | "network" | "silence";
  status?: number;
  /** True when the client has given up: no further attempt will be made. */
  terminal: boolean;
  error?: unknown;
}

export type SseStatus = "connecting" | "open" | "reconnecting" | "closed";

/** The subset of `RequestInit` this client sends. */
export interface SseRequest {
  method: "GET" | "POST";
  headers: Record<string, string>;
  body?: string;
  credentials: RequestCredentials;
  signal: AbortSignal;
  cache: RequestCache;
}

/** The subset of `Response` this client reads. `fetch` satisfies it. */
export interface SseResponse {
  ok: boolean;
  status: number;
  body: ReadableStream<Uint8Array> | null;
}

export type SseFetch = (url: string, init: SseRequest) => Promise<SseResponse>;

export interface OpenSseOptions {
  url: string;
  method?: "GET" | "POST";
  /** JSON body for a POST stream (the assistant). */
  body?: unknown;
  headers?: Record<string, string>;
  /** Resume cursor. For a cold open this is the `next` from the history page. */
  lastEventId?: string;
  kind?: SseKind;
  onMessage: (message: SseMessage) => void;
  onStatus?: (status: SseStatus) => void;
  onError?: (failure: SseFailure) => void;
  /** Events after which the client stops for good. Defaults to `run_ended`. */
  terminalEvents?: readonly string[];
  /** False for a stream that is complete when the server closes it cleanly. */
  reconnectOnClose?: boolean;
  silenceMs?: number;
  maxBackoffMs?: number;
  baseBackoffMs?: number;
  fetchImpl?: SseFetch;
  random?: () => number;
  /** Injected in tests so a backoff is observed without waiting for it. */
  wait?: (ms: number, signal: AbortSignal) => Promise<void>;
}

export interface SseHandle {
  /** Stop the stream and release its slot in the concurrency cap. */
  close(): void;
  /** Resolves when the client has stopped for good. */
  readonly closed: Promise<void>;
  readonly status: SseStatus;
  /** The id of the last event delivered — the resume cursor. */
  readonly lastEventId: string | undefined;
  /** Consecutive failed connection attempts; 0 while connected. */
  readonly attempts: number;
}

export class SseStreamLimitError extends Error {
  readonly kind: SseKind;
  readonly limit: number;
  constructor(kind: SseKind, limit: number) {
    super(
      `Too many ${kind} streams are open (the limit is ${limit}). Close one and try again.`,
    );
    this.name = "SseStreamLimitError";
    this.kind = kind;
    this.limit = limit;
  }
}

// ---------------------------------------------------------------------------
// Concurrency cap
// ---------------------------------------------------------------------------

const openStreams: Record<SseKind, number> = { run: 0, training: 0, chat: 0 };

/** How many streams of each kind are open. Exported for the status bar. */
export function activeSseStreams(): Readonly<Record<SseKind, number>> {
  return { ...openStreams };
}

/** Test-only: drop every accounted slot so one case cannot leak into the next. */
export function resetSseStreamRegistry(): void {
  openStreams.run = 0;
  openStreams.training = 0;
  openStreams.chat = 0;
}

// ---------------------------------------------------------------------------
// Backoff
// ---------------------------------------------------------------------------

export interface BackoffOptions {
  baseMs?: number;
  maxMs?: number;
  random?: () => number;
}

/**
 * Equal-jitter exponential backoff: half the window plus a random half.
 *
 * Full jitter (`random() * window`) can return ~0 and hammer an API that is
 * already failing; a fixed delay makes every console reconnect in lockstep.
 * The window doubles per attempt and is clamped at 30 s (§14.7).
 */
export function backoffDelay(attempt: number, opts: BackoffOptions = {}): number {
  const base = opts.baseMs ?? DEFAULT_BASE_BACKOFF_MS;
  const max = opts.maxMs ?? DEFAULT_MAX_BACKOFF_MS;
  const random = opts.random ?? Math.random;
  const exponent = Math.min(attempt, 32);
  const window = Math.min(max, base * 2 ** exponent);
  return Math.round(window / 2 + random() * (window / 2));
}

// ---------------------------------------------------------------------------
// Frame parsing
// ---------------------------------------------------------------------------

/**
 * Parse one SSE frame (the text between blank lines) per the WHATWG rules:
 * `field: value`, a leading space after the colon is stripped, repeated `data`
 * lines join with `\n`, and a line starting with `:` is a comment.
 *
 * Returns null for a comment-only frame — a keepalive still counts as traffic
 * for the silence watchdog, but there is nothing to deliver.
 */
export function parseSseFrame(raw: string): SseMessage | null {
  let id: string | undefined;
  let event: string | undefined;
  const data: string[] = [];

  for (const line of raw.split("\n")) {
    if (line === "" || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "id") id = value;
    else if (field === "event") event = value;
    else if (field === "data") data.push(value);
  }

  if (data.length === 0 && id === undefined && event === undefined) return null;
  const message: SseMessage = { event: event ?? "message", data: data.join("\n") };
  if (id !== undefined) message.id = id;
  return message;
}

// ---------------------------------------------------------------------------
// openSse
// ---------------------------------------------------------------------------

const defaultWait = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });

export function openSse(options: OpenSseOptions): SseHandle {
  const kind: SseKind = options.kind ?? "run";
  const limit = SSE_STREAM_LIMITS[kind];
  if (openStreams[kind] >= limit) throw new SseStreamLimitError(kind, limit);
  openStreams[kind] += 1;

  const method = options.method ?? "GET";
  const isPost = method === "POST";
  const fetchImpl = options.fetchImpl ?? ((url, init) => fetch(url, init as RequestInit));
  const wait = options.wait ?? defaultWait;
  const silenceMs = options.silenceMs ?? DEFAULT_SILENCE_MS;
  const terminalEvents = new Set(options.terminalEvents ?? DEFAULT_TERMINAL_EVENTS);
  // §14.7: a POST stream is a single request and response; it never reconnects.
  const reconnectOnClose = isPost ? false : (options.reconnectOnClose ?? true);
  const backoffOptions: BackoffOptions = {
    maxMs: options.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS,
    baseMs: options.baseBackoffMs ?? DEFAULT_BASE_BACKOFF_MS,
    ...(options.random ? { random: options.random } : {}),
  };

  const controller = new AbortController();
  let status: SseStatus = "connecting";
  let lastEventId = options.lastEventId;
  let attempts = 0;
  let done = false;
  let released = false;

  const setStatus = (next: SseStatus): void => {
    if (status === next) return;
    status = next;
    options.onStatus?.(next);
  };

  const release = (): void => {
    if (released) return;
    released = true;
    openStreams[kind] = Math.max(0, openStreams[kind] - 1);
  };

  const fail = (failure: SseFailure): void => {
    options.onError?.(failure);
  };

  /** Read one connection to its end. */
  async function pump(body: ReadableStream<Uint8Array>): Promise<"ended" | "silence" | "final"> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let outcome: "ended" | "silence" | "final" = "ended";

    const onAbort = (): void => void reader.cancel().catch(() => {});
    // `close()` can land between the fetch resolving and the first read. The
    // abort event has already been dispatched by then, so a listener alone
    // would never fire and the reader would block on a stream nobody feeds.
    if (controller.signal.aborted) {
      onAbort();
      return "final";
    }
    controller.signal.addEventListener("abort", onAbort, { once: true });

    try {
      for (;;) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const silenced = new Promise<{ silence: true }>((resolve) => {
          timer = setTimeout(() => resolve({ silence: true }), silenceMs);
        });
        const raced = await Promise.race([
          reader.read().then((r) => ({ read: r })),
          silenced,
        ]);
        if (timer !== undefined) clearTimeout(timer);

        if ("silence" in raced) {
          await reader.cancel().catch(() => {});
          return "silence";
        }
        if (controller.signal.aborted) return "final";

        const { done: finished, value } = raced.read;
        if (finished) {
          buffer += decoder.decode();
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        // Frames are separated by a blank line; \r\n is tolerated.
        const normalised = buffer.replace(/\r\n/g, "\n");
        const parts = normalised.split("\n\n");
        buffer = parts.pop() ?? "";
        for (const part of parts) {
          const message = parseSseFrame(part);
          if (!message) continue;
          if (message.id !== undefined) lastEventId = message.id;
          options.onMessage(message);
          if (terminalEvents.has(message.event)) {
            outcome = "final";
            await reader.cancel().catch(() => {});
            return outcome;
          }
        }
      }

      const tail = parseSseFrame(buffer.replace(/\r\n/g, "\n"));
      if (tail) {
        if (tail.id !== undefined) lastEventId = tail.id;
        options.onMessage(tail);
        if (terminalEvents.has(tail.event)) outcome = "final";
      }
      return outcome;
    } finally {
      controller.signal.removeEventListener("abort", onAbort);
      try {
        // Releasing a reader that still has a read request queued throws by
        // spec. That would surface as a bogus "network error" and a reconnect.
        reader.releaseLock?.();
      } catch {
        // The connection is finished either way.
      }
    }
  }

  async function loop(): Promise<void> {
    try {
      while (!done && !controller.signal.aborted) {
        setStatus(attempts === 0 ? "connecting" : "reconnecting");

        const headers: Record<string, string> = {
          Accept: "text/event-stream",
          ...options.headers,
        };
        if (lastEventId !== undefined) headers["Last-Event-ID"] = lastEventId;
        if (isPost && options.body !== undefined) headers["Content-Type"] = "application/json";

        const request: SseRequest = {
          method,
          headers,
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
          ...(isPost && options.body !== undefined
            ? { body: JSON.stringify(options.body) }
            : {}),
        };

        let response: SseResponse;
        try {
          response = await fetchImpl(options.url, request);
        } catch (error) {
          if (controller.signal.aborted) break;
          const terminal = !reconnectOnClose;
          fail({ kind: "network", terminal, error });
          if (terminal) break;
          attempts += 1;
          await wait(backoffDelay(attempts - 1, backoffOptions), controller.signal);
          continue;
        }

        if (controller.signal.aborted) break;

        if (!response.ok || !response.body) {
          // §13/§14.7: 401 and 404 are answers, not blips. Retrying a 404
          // forever is how a deleted run pins a browser tab at 100% CPU.
          const terminal = TERMINAL_STATUS.has(response.status) || !reconnectOnClose;
          fail({ kind: "http", status: response.status, terminal });
          if (terminal) break;
          attempts += 1;
          await wait(backoffDelay(attempts - 1, backoffOptions), controller.signal);
          continue;
        }

        attempts = 0;
        setStatus("open");
        const outcome = await pump(response.body);
        if (controller.signal.aborted || outcome === "final") break;

        if (outcome === "silence") fail({ kind: "silence", terminal: !reconnectOnClose });
        if (!reconnectOnClose) break;

        attempts += 1;
        await wait(backoffDelay(attempts - 1, backoffOptions), controller.signal);
      }
    } finally {
      done = true;
      setStatus("closed");
      release();
    }
  }

  const closed = loop();

  return {
    close(): void {
      done = true;
      controller.abort();
    },
    closed,
    get status() {
      return status;
    },
    get lastEventId() {
      return lastEventId;
    },
    get attempts() {
      return attempts;
    },
  };
}
